import { TranslationService } from './../shared/services/translation.service';
import { Injectable } from '@angular/core';
import { BackendResponse, BackendService } from '@tg4/http-infrastructure/dist/src';
import { Observable } from 'rxjs';
import { AuthService } from '../shared/services/auth.service';
import { UserPreview } from '../shared/interfaces/user-preview.interface';
import { map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SharedService } from '../shared/services/shared.service';
import { CalibrationFilters } from '../shared/interfaces/calibration-filters.interface';
import { isNil } from "lodash";
import { JobProgressNotification } from "src/app/shared/modals/job-follow-up/models/job-progress-notification";
import { RankEnum } from '../shared/enums/RankEnum';
import { RankPriorityEnum } from '../shared/enums/RankPriorityEnum';
import { GroupParticipant } from 'src/app/calibration/_interfaces/group-participant.interface';
import { FiltersCalibration } from './_interfaces/filters-calibration';
import { objectToBackendParams } from 'auth/src/lib/utils/filterUtils';
import { ConfigService } from '../shared/services/multi-clients/config.service';

@Injectable()
export class CalibrationService {
    apiUrl: string;

    constructor(
            private _httpService: BackendService,
            private _authService: AuthService,
            private _http: HttpClient,
            private _sharedService: SharedService,
            private _translation: TranslationService,
            private _configService: ConfigService
    ) {
        this.apiUrl = this._configService.settings.apiUrl;
    }

    public getCalibrationGroupUsers(
        filters: Partial<FiltersCalibration>
    ): Observable<any> {
        delete filters.createdAt;
        const params = objectToBackendParams(filters);
        return this._httpService.get('getCalibrationGroupUsers', [], params);
    }

    public getCalibrationGroupSummary(
        filters: Partial<FiltersCalibration>
    ): Observable<any> {
        const params = objectToBackendParams(filters);
        return this._httpService.get('getCalibrationGroupSummary', [], params);
    }

    public getCalibrationUserSummary(
            cycleParticipantId: number, cycleId: number, selectedLanguage: string
    ): Observable<any> {
        return this._httpService.get('getCalibrationUserSummary', [], [
            { 'name': 'cycleParticipantId', 'value': cycleParticipantId as any },
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'selectedLanguage', 'value': selectedLanguage as any }
        ]);
    }

    public getCalibrationGroupParticipantsReportPDF(
            cycleId: number,
            groupId: number,
            calibrationProcessId: number,
            page: number,
            pageSize: number,
            cycleParticipantId?: number,
            filters?: string,
            cacheKey?: string
    ): Observable<any> {
        return this._httpService.get('getCalibrationGroupParticipantsReportPDF', [], [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'groupId', 'value': groupId as any },
            { 'name': 'calibrationProcessId', 'value': calibrationProcessId as any },
            { 'name': 'cycleParticipantId', 'value': cycleParticipantId as any },
            { 'name': 'filters', 'value': filters as any },
            { 'name': 'cacheKey', 'value': cacheKey },
            { 'name': 'page', 'value': page as any },
            { 'name': 'pageSize', 'value': pageSize as any },
        ]);
    }

    public getCalibrationGroupParticipantsReport(
            cycleId: number,
            groupId: number,
            calibrationProcessId: number,
            cycleParticipantId?: number,
            filters?: string
    ): Observable<any> {
        return this._httpService.get('getCalibrationGroupParticipantsReport', [], [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'groupId', 'value': groupId as any },
            { 'name': 'calibrationProcessId', 'value': calibrationProcessId as any },
            { 'name': 'cycleParticipantId', 'value': cycleParticipantId as any },
            { 'name': 'filters', 'value': filters as any }
        ]);
    }

    public getParticipantEvaluationLog(cycleId: number, subjectUserId: string, selectedLanguage: string): Observable<any> {
        return this._httpService.get('getCalibrationGroupParticipantEvaluationLog', [], [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'subjectUserId', 'value': subjectUserId as any },
            { 'name': 'selectedLanguage', 'value': selectedLanguage as any },
        ]);
    }

    public getHasReviewAccess(userId: string): Observable<any> {
        return this._httpService.get('hasAccessToReview', [], [
            {'name': 'userId', 'value': userId as any}
        ]);
    }

    public importCalibrationGroupParticipants(
            data: string,
            cycleId: any
    ): Observable<any> {
        return this._httpService.post('importCalibrationGroupParticipants', {
            'data': data,
            'cycleId': cycleId
        });
    }

    public updateGroupParticipantQuadrant(args: {
        groupId: number,
        modifiedQuadrantId: number,
        userId: string
    }): Observable<any> {
        return this._httpService.post('updateGroupParticipantQuadrant', args);
    }
    
    public reorderGroupParticipants(
        groupId: number,
        participants: Array<GroupParticipant>
    ): Observable<any> {
        return this._httpService.post('reorderGroupParticipants', {
            'groupId': groupId,
            'participants': participants
        });
    }

    public updateGroupMeetingStatus(groupId: number, inProgress: boolean): Observable<any> {
        return this._httpService.put('updateGroupMeetingStatus', {
            'groupId': groupId,
            'inProgress': inProgress
        });
    }

    public updateCalibrationGroupNotes(groupId: number, notes: string): Observable<any> {
        return this._httpService.put('updateCalibrationGroupNotes', {
            'groupId': groupId,
            'notes': notes
        });
    }

    public updateCalibrationGroupUserNotes(
            groupId: number, userId: string, notes: string, inReview: boolean,
            reviewNote: string
    ): Observable<any> {
        return this._httpService.post('updateCalibrationGroupUserNotes', {
            'groupId': groupId,
            'userId': userId,
            'notes': notes,
            'inReview': inReview,
            'reviewNote': reviewNote
        });
    }

    public updateCalibrationGroupUserRank(
        groupId: number, userId: string, inPromotion: boolean,
        nextRank: RankEnum,
        nextRankPriority: RankPriorityEnum,
    ): Observable<any> {
    return this._httpService.post('updateCalibrationGroupUserNotes', {
        'groupId': groupId,
        'userId': userId,
        'inPromotion': inPromotion,
        'nextRank': nextRank,
        'nextRankPriority': nextRankPriority,
    });
}

    public exportGroups(cycleId: number): Observable<any> {
        // TODO REPLACE WITH ACTUAL ENDPOINT
        return this._downloadFile({
            'optionalFields': [],
            'cycleId': cycleId,
            'reportId': 1,
            'directSubordinates': true,
            'attributeIds': []
        });
    }

    public exportPresentation({ cycleId, groupId, calibrationProcessId, cycleParticipantId, filters }: {
        cycleId: number,
        groupId: number,
        calibrationProcessId: number,
        cycleParticipantId?: number,
        filters?: CalibrationFilters
    }): Observable<BackendResponse<string>> {
        const translate = this._translation.getSelectedLanguage();
        return this._httpService.post<string>('getCalibrationGroupReportPDF',{
            'cycleId': cycleId,
            'groupId': groupId,
            'cycleParticipantId': cycleParticipantId,
            'calibrationProcessId': calibrationProcessId,
            'filters': filters,
            'translate': translate ? translate:'pt-BR'
        });
    }

    public getJobStatus(jobId: string) {
        return this._httpService.get<JobProgressNotification>('getCalibrationJobStatus',
                [jobId], []);
    }

    private _downloadFile(data: any) {
        const applicationsUrl = `${this.apiUrl}/api/report/getReport`;
        const user = this._authService.getLoggedUser();
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': user.access_token,
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const impersonatedUser: UserPreview | null = this._authService.getImpersonatedUser();
        if (impersonatedUser && impersonatedUser.userId) {
            headers.append('UserIdToImpersonate', impersonatedUser.userId);
        }
        const options = {
            headers: headers,
            observe: 'response' as 'body',
            responseType: 'blob' as 'json'
        };

        return this._http.post(applicationsUrl, data, options)
                .pipe(map((res) => this._sharedService.downloadFile(res, { defaultFileName: 'presentation.pdf' })));
    }
}
